import React, { useState, useEffect } from "react";
import { Store } from "lucide-react";
import { useLanguage } from '../context/LanguageContext';
import API_BASE_URL from '../services/apiconfig';

const KooturavuMarunthagam = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [kooturaavuData, setKooturaavuData] = useState(null);
  const [medicals, setMedicals] = useState([]);

  const MEDIA_BASE_URL = 'https://kooturavu.tn.gov.in';

  useEffect(() => {
    fetchKooturaavuData();
    fetchMedicals();
  }, []);

  const fetchKooturaavuData = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/kooturavu_marunthagam/get_content`);
      const result = await response.json();
      
      if (result.status === 'success' && result.data) {
        setKooturaavuData(result.data);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching Kooturavu Marunthagam data:', error);
      setLoading(false);
    }
  };

  const fetchMedicals = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/kooturavu_marunthagam/medicals/get_all`);
      const result = await response.json();
      
      if (result.status === 'success' && result.data) {
        setMedicals(result.data);
      }
    } catch (error) {
      console.error('Error fetching medicals:', error);
    }
  };

  const content = {
    en: {
      overview: "Overview",
      keyStats: "Key Stats",
      storesLabel: "Kooturavu Marunthagam Stores",
      tableTitle: "District-wise Cooperative Medicals",
      tableHeaders: {
        sno: "S.No.",
        region: "Region",
        institution: "Institution",
        shopName: "Shop Name"
      }
    },
    ta: {
      overview: "கண்ணோட்டம்",
      keyStats: "முக்கிய புள்ளிவிவரங்கள்",
      storesLabel: "கூட்டுறவு மருந்தகம் கடைகள்",
      tableTitle: "மாவட்ட வாரியாக கூட்டுறவு மருந்தகங்கள்",
      tableHeaders: {
        sno: "வ.எண்.",
        region: "பிராந்தியம்",
        institution: "நிறுவனம்",
        shopName: "கடை பெயர்"
      }
    }
  };

  const t = content[language];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (!kooturaavuData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">No Kooturavu Marunthagam data available</div>
      </div>
    );
  }

  return (
    <section id="kooturavu-section" className="bg-white p-4 sm:p-6 lg:p-8 rounded-lg shadow-sm">

      {/* Overview */}
      <div className="mb-10">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.overview}</h3>
        <div className="bg-green-50 p-6 rounded-lg">
          <p className="text-gray-700 leading-relaxed">
            {language === 'en' ? kooturaavuData.overview_eng : kooturaavuData.overview_tamil}
          </p>
        </div>
      </div>

      {/* Key Stats - Left Aligned */}
      <div className="mb-10">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.keyStats}</h3>
        {kooturaavuData.key_stats && kooturaavuData.key_stats.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {kooturaavuData.key_stats.map((stat, index) => (
              <div key={index} className="bg-lime-100 p-6 text-center rounded-lg">
                {stat.logo_path ? (
                  <div className="flex justify-center mb-3">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                      <img 
                        src={`${MEDIA_BASE_URL}/${stat.logo_path}`} 
                        alt="stat logo" 
                        className="w-8 h-8 object-contain"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-center mb-3">
                    <div className="w-12 h-12 bg-white text-gray-700 rounded-full flex items-center justify-center">
                      <Store />
                    </div>
                  </div>
                )}
                <div className="text-2xl font-bold text-gray-900">
                  {language === 'en' ? stat.heading_eng : stat.heading_tamil}
                </div>
                <div className="text-sm font-semibold text-gray-700 mt-2">
                  {language === 'en' ? stat.content_eng : stat.content_tamil}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex justify-center">
            <div className="bg-lime-100 p-6 text-center rounded-lg w-full max-w-sm">
              <div className="flex justify-center mb-3">
                <div className="w-12 h-12 bg-white text-gray-700 rounded-full flex items-center justify-center">
                  <Store />
                </div>
              </div>
              <div className="text-2xl font-bold text-gray-900">{medicals.length}</div>
              <div className="text-sm font-semibold text-gray-700 mt-2">{t.storesLabel}</div>
            </div>
          </div>
        )}
      </div>

      {/* District-wise Cooperative Medicals */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.tableTitle}</h3>
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
            <table className="w-full border-collapse text-sm">
              <thead className="sticky top-0 bg-gray-200">
                <tr>
                  <th className="p-2 sm:p-3 text-left text-gray-800 font-semibold border border-gray-300 whitespace-nowrap">{t.tableHeaders.sno}</th>
                  <th className="p-2 sm:p-3 text-left text-gray-800 font-semibold border border-gray-300">{t.tableHeaders.region}</th>
                  <th className="p-2 sm:p-3 text-left text-gray-800 font-semibold border border-gray-300">{t.tableHeaders.institution}</th>
                  <th className="p-2 sm:p-3 text-left text-gray-800 font-semibold border border-gray-300">{t.tableHeaders.shopName}</th>
                </tr>
              </thead>
              <tbody>
                {medicals.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="p-3 text-center text-gray-500">
                      No medical shops available
                    </td>
                  </tr>
                ) : (
                  medicals.map((item) => (
                    <tr key={item.id} className="border-b border-gray-300 hover:bg-gray-50">
                      <td className="p-2 sm:p-3 text-gray-800 font-medium border border-gray-300">{item.Sl_No}</td>
                      <td className="p-2 sm:p-3 text-gray-700 border border-gray-300">{item.Name_of_the_Region}</td>
                      <td className="p-2 sm:p-3 text-gray-700 border border-gray-300">{item.Name_of_the_institutions}</td>
                      <td className="p-2 sm:p-3 text-gray-700 border border-gray-300">{item.Name_of_the_Shops}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

    </section>
  );
};

export default KooturavuMarunthagam;